import React, { useState } from "react";
import {
  Create,
  BooleanInput,
  NumberInput,
  TextInput,
  TabbedForm,
  FormTab,
  ImageField,
  ImageInput,
  FileInput,
  FileField,
  maxLength,
  required,
  useNotify,
  useRedirect,
  ArrayInput,
  SimpleFormIterator,
  minValue,
  maxValue,
  regex,
} from "react-admin";
import { RichTextInput } from "ra-input-rich-text";
import { CachedCategoriesInput } from "../common/CachedCategoriesInput/CachedCategoriesInput";

const ensureBoolean = (value) => !!value;

const ensureNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return 0;
  }
  const num = parseFloat(value);
  return isNaN(num) ? 0 : num;
};

const cleanValue = (value) => {
  if (!value) return null;
  if (value.rawFile instanceof File) return value;
  if (value.src) return value.src;
  if (typeof value === "string") return value;
  return null;
};

const cleanArray = (array) => {
  if (!Array.isArray(array)) return [];
  return array.map((item) => cleanValue(item)).filter(Boolean);
};

export const ProductCreate = (props) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const [localReviews, setLocalReviews] = useState([]);

  const onSuccess = (data) => {
    notify("Producto creado exitosamente", {
      type: "info",
      messageArgs: { smart_count: 1, name: data.name },
    });
    redirect("list", props.basePath || "/products", data.id, data);
  };

  const handleReviewsChange = (newReviews) => {
    setLocalReviews(newReviews);
  };

  return (
    <Create
      {...props}
      mutationOptions={{ onSuccess }}
      transform={(data) => {
        return {
          ...data,
          category: data.categoryId ? { id: data.categoryId } : null,
          featuredLink: "",
          reviews: localReviews.length > 0 ? localReviews : data.reviews,

          enabled: ensureBoolean(data.enabled),
          featured: ensureBoolean(data.featured),
          featuredVideo: ensureBoolean(data.featuredVideo),
          price: ensureNumber(data.price),
          stock: ensureNumber(data.stock),
          minMediumStock: ensureNumber(data.minMediumStock),
          minHighStock: ensureNumber(data.minHighStock),
          quantityPerPackage: ensureNumber(data.quantityPerPackage),
          totalReviews: ensureNumber(data.totalReviews),
          averageRating: ensureNumber(data.averageRating),
          manual: cleanValue(data.manual),
          images: cleanArray(data.images),
          hdImages: cleanArray(data.hdImages),
          catalogImages: cleanArray(data.catalogImages),
        };
      }}
    >
      <TabbedForm>
        <FormTab label="General">
          <div style={{ display: "flex", gap: "30px", flexDirection: "row" }}>
            <TextInput
              source="code"
              label="Código"
              validate={[required(), maxLength(10)]}
            />
            <CachedCategoriesInput
              source="categoryId"
              label="Categoría"
              allowEmpty
              perPage={100}
              sort={{ field: "name", order: "ASC" }}
              validate={[required()]}
            />
          </div>
          <div
            style={{
              display: "flex",
              gap: "30px",
              flexDirection: "row",
              width: "100%",
            }}
          >
            <TextInput
              source="name"
              label="Nombre"
              validate={[required(), maxLength(100)]}
            />
            <TextInput
              source="slug"
              label="Slug"
              validate={[
                required(),
                maxLength(100),
                regex(
                  /^[a-z0-9-]+$/,
                  "Solo se permiten letras minúsculas, números y guiones. Sin espacios."
                ),
              ]}
            />
          </div>
          <div style={{ display: "flex", gap: "30px", flexDirection: "row" }}>
            <BooleanInput
              source="enabled"
              defaultValue={true}
              label="Habilitado"
            />
            <BooleanInput source="featured" label="Destacado" />
            <BooleanInput source="featuredVideo" label="Video Destacado" />
          </div>
          <TextInput
            source="videoLink"
            label="Enlace de Video"
            validate={[maxLength(200)]}
          />
          <div
            style={{
              display: "flex",
              gap: "30px",
              flexDirection: "row",
              width: "100%",
            }}
          >
            <NumberInput
              source="price"
              label="Precio"
              validate={[required()]}
            />
            <NumberInput source="stock" label="Stock" validate={[required()]} />
            <NumberInput
              source="minMediumStock"
              label="Stock Mínimo Medio"
              validate={[required()]}
            />
            <NumberInput
              source="minHighStock"
              label="Stock Mínimo Alto"
              validate={[required()]}
            />
            <NumberInput
              source="quantityPerPackage"
              label="Cantidad por Paquete"
              validate={[required()]}
            />
          </div>
        </FormTab>
        <FormTab label="Características">
          <RichTextInput
            source="description"
            label="Descripción"
            validate={[required()]}
            options={{
              locale: "es-ES",
            }}
          />
          <RichTextInput
            source="catalogDescription"
            label="Descripción del Catálogo"
            validate={[maxLength(725)]}
            options={{
              locale: "es-ES",
            }}
          />
          <ArrayInput
            source="technicalSpecifications"
            label="Especificaciones técnicas"
          >
            <SimpleFormIterator>
              <div
                style={{ display: "flex", gap: "30px", flexDirection: "row" }}
              >
                <TextInput
                  style={{ width: "200px" }}
                  source="label"
                  label="Etiqueta"
                  validate={[required()]}
                />
                <TextInput
                  style={{ width: "200px" }}
                  source="value"
                  label="Valor"
                  validate={[required()]}
                />
              </div>
            </SimpleFormIterator>
          </ArrayInput>
        </FormTab>
        <FormTab label="Manual">
          <FileInput
            source="manual"
            label="Manual (PDF)"
            accept={{
              "application/pdf": [".pdf"],
            }}
            placeholder="Haz clic aquí o arrastra y suelta el manual (PDF)"
            options={{
              preventDropOnDocument: true,
              maxFiles: 1,
              validator: (file) => {
                if (file.type !== "application/pdf") {
                  return {
                    code: "invalid-file-type",
                    message: "Solo se permiten archivos PDF",
                  };
                }
                return null;
              },
            }}
          >
            <FileField source="src" title="title" />
          </FileInput>
        </FormTab>
        <FormTab label="Imágenes">
          <ImageInput
            multiple
            source="images"
            label="Imágenes"
            accept={{
              "image/*": [".png", ".jpg", ".jpeg", ".gif"],
            }}
            placeholder="Haz clic aquí o arrastra y suelta las imágenes"
            options={{
              preventDropOnDocument: true,
              validator: (file) => {
                if (!file.type.startsWith("image/")) {
                  return {
                    code: "invalid-file-type",
                    message: "Solo se permiten imágenes",
                  };
                }
                return null;
              },
            }}
          >
            <ImageField source="src" title="title" />
          </ImageInput>
          <ImageInput
            multiple
            source="hdImages"
            label="Imágenes HD"
            accept={{
              "image/*": [".png", ".jpg", ".jpeg", ".gif"],
            }}
            placeholder="Haz clic aquí o arrastra y suelta las imágenes"
            options={{
              preventDropOnDocument: true,
              validator: (file) => {
                if (!file.type.startsWith("image/")) {
                  return {
                    code: "invalid-file-type",
                    message: "Solo se permiten imágenes",
                  };
                }
                return null;
              },
            }}
          >
            <ImageField source="src" title="title" />
          </ImageInput>
          <ImageInput
            multiple
            source="catalogImages"
            label="Imágenes del Catálogo"
            accept={{
              "image/*": [".png", ".jpg", ".jpeg", ".gif"],
            }}
            placeholder="Haz clic aquí o arrastra y suelta las imágenes"
            options={{
              preventDropOnDocument: true,
              validator: (file) => {
                if (!file.type.startsWith("image/")) {
                  return {
                    code: "invalid-file-type",
                    message: "Solo se permiten imágenes",
                  };
                }
                return null;
              },
            }}
          >
            <ImageField source="src" title="title" />
          </ImageInput>
        </FormTab>
        <FormTab label="Opiniones">
          <NumberInput
            source="totalReviews"
            label="Total de Opiniones"
            min={0}
            validate={[minValue(0)]}
          />
          <NumberInput
            source="averageRating"
            label="Promedio de Calificación"
            min={0}
            max={5}
            step={0.1}
            validate={[minValue(0), maxValue(5)]}
          />
          <ArrayInput
            source="reviews"
            label="Opiniones de Usuarios"
            onChange={handleReviewsChange}
            defaultValue={localReviews}
            max={3}
          >
            <SimpleFormIterator max={3}>
              <TextInput
                source="author"
                label="Autor"
                validate={[required()]}
              />
              <NumberInput
                source="rating"
                label="Calificación"
                min={0}
                max={5}
                step={1}
                validate={[required(), minValue(0), maxValue(5)]}
              />
              <TextInput
                source="comment"
                label="Comentario"
                multiline
                validate={[required()]}
              />
            </SimpleFormIterator>
          </ArrayInput>
        </FormTab>
      </TabbedForm>
    </Create>
  );
};
